/**
 * The sanitized diagnostic bundle (web-client.md §8, logging §8). A downloadable JSON blob that joins
 * client state to server logs for support: versions, effective flags, the last N cmdId/ack pairs,
 * connection stats, a redacted log tail, and the capability + check summary.
 *
 * REDACTION IS THE POINT: this artifact leaves the user's machine, so it must contain NO access/refresh
 * token, NO Authorization header, NO player-authored chat text, and NO raw PII. The builder is pure
 * and defensively scrubs every string field it emits (see {@link redactLine}); a unit test asserts a
 * planted token never survives. DOM-free.
 */
import type { CommandRecord } from './commandLog.ts';
import type { CheckResult } from './types.ts';

export interface BundleInput {
  clientVersion: string;
  protocolVersion: number;
  serverUrl: string;
  flags: Record<string, boolean>;
  connection: { status: string; attempt: number; lastError: string | null };
  commands: CommandRecord[];
  /** Rolling log tail (newest last). Redacted line-by-line before inclusion. */
  logTail: string[];
  checks: CheckResult[];
  capabilities: Record<string, unknown>;
  /** ISO timestamp the bundle was produced. */
  generatedAt: string;
}

export interface DiagnosticBundle {
  schema: 'ts.web.diagnostics/1';
  generatedAt: string;
  client: { version: string; protocolVersion: number; serverUrl: string };
  connection: { status: string; attempt: number; lastError: string | null };
  flags: Record<string, boolean>;
  /** Only the flags-hash-relevant summary; the map above is booleans (no PII). */
  commands: CommandRecord[];
  checks: { id: string; status: string; detail: string }[];
  capabilities: Record<string, unknown>;
  logTail: string[];
}

// Patterns that must never appear verbatim in an exported bundle.
const REDACTIONS: { re: RegExp; with: string }[] = [
  // Authorization: Bearer <token>
  { re: /\b[Bb]earer\s+[A-Za-z0-9._~+/-]+=*/g, with: 'Bearer «redacted»' },
  // JWT-shaped triple-segment tokens (header.payload.signature).
  { re: /\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, with: '«jwt»' },
  // token/access/refresh/password key=value or "key":"value" pairs.
  {
    re: /("?\b(?:access|refresh|token|password|secret|authorization)\b"?\s*[:=]\s*)"?[^\s",}]+"?/gi,
    with: '$1«redacted»',
  },
  // Bare email addresses (PII).
  { re: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, with: '«email»' },
];

/** Scrub one log/string line of any token/PII shape. Exported for direct testing. */
export function redactLine(line: string): string {
  let out = line;
  for (const rule of REDACTIONS) out = out.replace(rule.re, rule.with);
  return out;
}

/**
 * Build the bundle. Pure. The command records are already token-free by construction (CommandLog only
 * stores cmdId/type/phase), but we still pass every emitted string through {@link redactLine} so a bug
 * upstream cannot leak a secret into the export.
 */
export function buildDiagnosticBundle(input: BundleInput): DiagnosticBundle {
  return {
    schema: 'ts.web.diagnostics/1',
    generatedAt: input.generatedAt,
    client: {
      version: input.clientVersion,
      protocolVersion: input.protocolVersion,
      serverUrl: redactLine(input.serverUrl),
    },
    connection: {
      status: input.connection.status,
      attempt: input.connection.attempt,
      lastError: input.connection.lastError ? redactLine(input.connection.lastError) : null,
    },
    flags: { ...input.flags },
    commands: input.commands.map((c) => ({
      cmdId: c.cmdId,
      type: c.type,
      phase: c.phase,
      at: c.at,
    })),
    checks: input.checks.map((c) => ({ id: c.id, status: c.status, detail: redactLine(c.detail) })),
    capabilities: { ...input.capabilities },
    logTail: input.logTail.map(redactLine),
  };
}

/** Serialize the bundle for download (stable 2-space JSON). */
export function serializeBundle(bundle: DiagnosticBundle): string {
  return JSON.stringify(bundle, null, 2);
}
